import React, { useState, useEffect } from "react";
import cx from "classnames";
import { useInView } from "react-intersection-observer";
import { Image } from "src/components/image";
import { BlockContent } from "src/components/block-content";
import { Container } from "src/components/container";
import { ContactForm } from "src/components/contact-form";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

import { Collapse } from "@material-ui/core";

import { ReactComponent as PlusLarge } from "src/images/svg/PlusLarge.svg";

import * as styles from "./contact.module.css";

interface LocationData {
	_key: string;
	address: string;
	citySvg: {
		alt: string;
		asset: {
			url: string;
		};
	};
	email: string;
	phone: string;
	timezone: string;
}

const LocationCard = ({ data }: { data: LocationData }) => {
	const [inViewRef, inView] = useInView({ triggerOnce: false });

	const [currentTime, setCurrentTime] = useState(dayjs().utc());
	const [greeting, setGreeting] = useState("");

	const MINUTE_MS = 1000;
	useEffect(() => {
		const interval = setInterval(() => {
			setCurrentTime(dayjs().utc());
		}, MINUTE_MS);

		return () => clearInterval(interval); // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
	}, []);

	useEffect(() => {
		const currentHour = dayjs().utc().tz(data.timezone).hour();
		if (currentHour < 6) {
			setGreeting("ZZZZZZZ");
		} else if (currentHour < 12) {
			setGreeting("Good Morning");
		} else if (currentHour < 17) {
			setGreeting("Good Afternoon");
		} else {
			setGreeting("Good Evening");
		}
	}, [currentTime]);

	return (
		<div className={cx(styles.locationCard, inView ? styles.inView : "")}>
			<img height="33" src={data.citySvg.asset.url} />
			<div className={styles.locationCardContent}>
				<p className={cx("small-body")}>
					{data.address?.split("\n").map(function (item, key) {
						return (
							<span key={key}>
								{item}
								<br />
							</span>
						);
					})}
					<a href={`mailto:${data.email}`}>{data.email}</a>
				</p>
				<div ref={inViewRef} className={cx("lcd-small", styles.timeGreeting)}>
					<span>
						{greeting.split(" ").map((word, index) => (
							<span key={`word-${index}`}>
								{word.split("").map((letter, index) => (
									<span
										key={`letter-${index}`}
										className={styles.letter}
										style={{
											animationDelay: `${Math.floor(Math.random() * 800)}ms`,
										}}
									>
										{letter}
									</span>
								))}{" "}
							</span>
						))}
					</span>
					<span>
						{currentTime
							.tz(data.timezone)
							.format("h:mmA")
							.split("")
							.map((letter, index) => (
								<span
									key={`time-character-${index}`}
									className={styles.letter}
									style={{
										animationDelay: `${Math.floor(Math.random() * 800)}ms`,
									}}
								>
									{letter}
								</span>
							))}
					</span>
				</div>
			</div>
		</div>
	);
};

const ContactFormCollapse = ({
	title,
	toEmail,
	bilingual,
	finePrint,
	successMessage,
}: {
	title: string;
	toEmail: string;
	bilingual: boolean;
	finePrint: string;
	successMessage: any[];
}) => {
	const [open, setOpen] = useState(false);

	return (
		<div className={styles.contactCollapse}>
			<button
				onClick={() => setOpen(!open)}
				className={cx("reset", styles.collapseTitle, open ? styles.open : "")}
			>
				<h4 className="title">
					<span className="underline-on-hover">{title}</span>
				</h4>
				<PlusLarge className={styles.plus} />
			</button>
			<Collapse in={open}>
				<div className={styles.form}>
					<ContactForm
						toEmail={toEmail}
						bilingual={bilingual}
						successMessage={successMessage}
						finePrint={finePrint}
					/>
				</div>
			</Collapse>
		</div>
	);
};

interface CareersData {
	copy: any[];
	title: string;
}

const CareersCollapse = ({ data }: { data: CareersData }) => {
	const [open, setOpen] = useState(false);

	return (
		<div className={styles.contactCollapse}>
			<button
				onClick={() => setOpen(!open)}
				className={cx("reset", styles.collapseTitle, open ? styles.open : "")}
			>
				<h4 className="title">
					<span className="underline-on-hover">{data.title}</span>
				</h4>
				<PlusLarge className={styles.plus} />
			</button>
			<Collapse in={open}>
				<BlockContent
					blocks={data.copy}
					className={cx("small-body", styles.careersCopy)}
				/>
			</Collapse>
		</div>
	);
};

export interface ContactData {
	headline: any[];
	backgroundImage: {
		alt: string;
		asset: {
			_id: string;
		};
	};
	careers: CareersData;
	generalContactFormTitle: string;
	generalContactFormFinePrint: string;
	generalContactFormTargetEmail: string;
	generalContactFormSuccessMessage: any[];
	EMEAContactFormTitle: string;
	EMEAContactFormFinePrint: string;
	EMEAContactFormTargetEmail: string;
	EMEAContactFormSuccessMessage: any[];
	locations: LocationData[];
}

export const Contact = ({ data }: { data: ContactData }) => {
	const { ref, inView } = useInView({
		rootMargin: "400px 0px",
	});

	const {
		headline,
		backgroundImage,
		careers,
		generalContactFormTitle,
		generalContactFormFinePrint,
		generalContactFormTargetEmail,
		generalContactFormSuccessMessage,
		EMEAContactFormTitle,
		EMEAContactFormFinePrint,
		EMEAContactFormTargetEmail,
		EMEAContactFormSuccessMessage,
		locations,
	} = data;

	return (
		<div ref={ref} className={cx("ac", styles.contact)}>
			<Container>
				{inView && (
					<Image
						alt="Contact page background image"
						imageId={backgroundImage.asset._id}
						className={styles.backgroundImage}
					/>
				)}
				<h1>
					<BlockContent
						blocks={headline}
						className={cx(
							"headline ac c-white animate-letters",
							styles.headline
						)}
						splitLetters={true}
					/>
				</h1>
				<div>
					<ContactFormCollapse
						title={generalContactFormTitle}
						toEmail={generalContactFormTargetEmail}
						bilingual={false}
						finePrint={generalContactFormFinePrint}
						successMessage={generalContactFormSuccessMessage}
					/>
					<ContactFormCollapse
						title={EMEAContactFormTitle}
						toEmail={EMEAContactFormTargetEmail}
						bilingual={true}
						finePrint={EMEAContactFormFinePrint}
						successMessage={EMEAContactFormSuccessMessage}
					/>
					<CareersCollapse data={careers} />
				</div>
				<div className={styles.locations}>
					{locations &&
						locations.map((location) => (
							<LocationCard data={location} key={location._key} />
						))}
				</div>
			</Container>
		</div>
	);
};
